import React from 'react';
import { Form, Input, Switch, Typography } from 'antd';
import { IdcardOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ObjectCardTile } from '../../../components/layout/ObjectCard';
import type { ProductCategory } from '../productCategoriesApi';
import CategoryTagPicker from './CategoryTagPicker';

interface IdentitySectionProps {
  categories: ProductCategory[];
  disabled: boolean;
  isNew: boolean;
}

/** „Dane produktu”: name, category (coloured tags) and codes; the active switch sits in the section header. */
const IdentitySection: React.FC<IdentitySectionProps> = ({ categories, disabled, isNew }) => {
  const { t } = useTranslation(['products']);
  return (
    <section className="obj-section product-card-identity" data-testid="product-section-identity">
      <header className="obj-section__header">
        <ObjectCardTile tone="blue" size="sm"><IdcardOutlined /></ObjectCardTile>
        <h2 className="obj-section__title">{t('products:card.identityTitle')}</h2>
        <div className="obj-section__actions">
          <label className="product-card-switch">
            <Form.Item name="isActive" valuePropName="checked" noStyle>
              <Switch size="small" disabled={disabled} aria-label={t('products:card.active')} />
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, next) => prev.isActive !== next.isActive}>
              {({ getFieldValue }) => (
                <span>{getFieldValue('isActive') === false ? t('products:card.inactive') : t('products:card.active')}</span>
              )}
            </Form.Item>
          </label>
        </div>
      </header>
      <div className="obj-form">
        <Form.Item
          name="name"
          label={t('products:card.nameLabel')}
          className="product-card-name"
          rules={[{ required: true, whitespace: true, message: t('products:validation.nameRequired') }]}
        >
          <Input placeholder={t('products:card.namePlaceholder')} disabled={disabled} maxLength={255} />
        </Form.Item>
        <Form.Item
          name="productCategoryId"
          label={t('products:card.categoryLabel')}
          rules={isNew ? [{ required: true, message: t('products:validation.categoryRequired') }] : []}
          extra={categories.length === 0
            ? t('products:card.categoryEmpty')
            : (
              <Typography.Link href="/app/products?tab=categories" target="_blank" rel="noopener noreferrer">
                {t('products:card.categoryManage')} →
              </Typography.Link>
            )}
        >
          <CategoryTagPicker categories={categories} disabled={disabled} ariaLabel={t('products:card.categoryLabel')} />
        </Form.Item>
        <Form.Item name="ean" label={t('products:card.ean')}>
          <Input disabled={disabled} maxLength={64} />
        </Form.Item>
        <Form.Item
          name="cnCode"
          label={t('products:card.cnCode')}
          rules={[{ pattern: /^\d{8}$/, message: t('products:card.cnCodeInvalid') }]}
        >
          <Input disabled={disabled} maxLength={8} inputMode="numeric" />
        </Form.Item>
        <Form.Item name="englishName" label={t('products:card.englishName')} tooltip={t('products:card.englishNameHint')}>
          <Input disabled={disabled} maxLength={255} />
        </Form.Item>
      </div>
    </section>
  );
};

export default IdentitySection;
